import type { FC } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { WithTestId } from 'types/app';

import { Modal } from '@Atomic/organisms/Modal';
import { Button } from '@Atomic/atoms/Button';
import { Title } from '@Atomic/atoms/Headline';
import { Text } from '@Atomic/atoms/Text';

import box from './img/good-price-box.png';
import box2x from './img/good-price-box@2x.png';
import loop from './img/good-price-loop.svg';
import styles from './goodPriceModal.module.scss';

export type GoodPriceModalProps = WithTestId<{
  visible: boolean;
  onClose: () => void;
}>;

/**
 * «Почему цена хорошая?» explainer (BP-8273). `Modal` turns a cover modal into a
 * bottom sheet on xs/sm, so one component serves both Figma layouts.
 */
export const GoodPriceModal: FC<GoodPriceModalProps> = ({
  visible,
  onClose,
  testId = 'good-price-modal',
}) => {
  const { t } = useTranslation();
  const titleId = useId();

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      width={632}
      className={styles.modal}
      containerClassName={styles.container}
      cover={
        <div className={styles.cover}>
          <img className={styles.loop} src={loop} alt="" role="presentation" />
          <img className={styles.box} src={box} srcSet={`${box2x} 2x`} alt="" role="presentation" />
        </div>
      }
      footer={
        <Button fullWidth onClick={onClose} testId={getTestIdByParts(testId, 'ok')}>
          {t('buttons.good')}
        </Button>
      }
      closeButtonTestId={getTestIdByParts(testId, 'close')}
      ariaLabelledBy={titleId}
      testId={testId}
    >
      <div className={styles.content}>
        <Title.H2 id={titleId} className={styles.title} testId={getTestIdByParts(testId, 'title')}>
          {t('offer.goodPrice.title')}
        </Title.H2>

        <Text className={styles.description} testId={getTestIdByParts(testId, 'description')}>
          {t('offer.goodPrice.description')}
        </Text>
      </div>
    </Modal>
  );
};
